import User from 'auth/User';
import useBodyClass from 'helpers/hooks/useBodyClass';
import { LayoutProvider } from 'layouts/LayoutProvider';
import Logout from 'pages/Logout/Logout';
import ForgotPassword from 'pages/ResetPassword/ForgotPasswordPage';
import ResetPassword from 'pages/ResetPassword/ResetPasswordPage';
import SetPassword from 'pages/SetPassword/SetPassword';
import PageNotFound from 'pages/errorPages/404';
import React from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import AuthenticatedProtectedRoute from 'routes/AuthenticatedProtectedRoute';
import AuthenticatedPublicRoute from 'routes/AuthenticatedPublicRoute';
import { AVAILABLE_PAGES_ENUM, ROUTE_GROUPS_ENUM } from 'routes/enums';
import permissionService from 'routes/permissionService';
import SignIn from 'screens/BrokerPortal/screens/SignIn/screens/SignIn';
import ScrollToTop from 'shared/ScrollToTop/ScrollToTop';
import LocationTracker from 'utils/tracking/LocationTracker';
import * as p from './routePaths';
import { ProtectedRouteMapProps } from './types';

type ProtectedGroupsMapType = [ROUTE_GROUPS_ENUM, ProtectedRouteMapProps[]][];

const protectedGroupsMap: ProtectedGroupsMapType = [
  [
    ROUTE_GROUPS_ENUM.DEFAULT,
    [
      { path: p.profile, pageName: AVAILABLE_PAGES_ENUM.PROFILE },
      { path: p.dashboard, pageName: AVAILABLE_PAGES_ENUM.DASHBOARD },
      { path: p.triage, pageName: AVAILABLE_PAGES_ENUM.TRIAGE },
    ],
  ],
  [ROUTE_GROUPS_ENUM.GUIDES, [{ path: p.guides, pageName: AVAILABLE_PAGES_ENUM.GUIDES }]],
  [
    ROUTE_GROUPS_ENUM.NETWORKS,
    [
      { path: `${p.registerNetworkStep1}`, pageName: AVAILABLE_PAGES_ENUM.NETWORK_REGISTER_STEP_1 },
      { path: `${p.networks.list}`, pageName: AVAILABLE_PAGES_ENUM.NETWORK_LIST },
      { path: `${p.networks.details}/:id`, pageName: AVAILABLE_PAGES_ENUM.NETWORK_DETAIL },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.CLUBS,
    [
      { path: `${p.clubs.list}`, pageName: AVAILABLE_PAGES_ENUM.CLUBS_LIST },
      { path: `${p.clubs.details}/:id`, pageName: AVAILABLE_PAGES_ENUM.CLUBS_DETAIL },
      { path: `${p.clubRegistrationStep1}`, pageName: AVAILABLE_PAGES_ENUM.CLUBS_REGISTER_STEP_1 },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.FIRMS,
    [
      { path: `${p.firms.list}`, pageName: AVAILABLE_PAGES_ENUM.FIRMS_LIST, exact: true },
      { path: `${p.firms.managersList}`, pageName: AVAILABLE_PAGES_ENUM.FIRM_MANAGERS },
      { path: `${p.firms.details}/:id`, pageName: AVAILABLE_PAGES_ENUM.FIRMS_DETAIL },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.ADMINS,
    [
      { path: `${p.admins.list}`, pageName: AVAILABLE_PAGES_ENUM.ADMINS_LIST, exact: true },
      { path: `${p.admins.list}/:id`, pageName: AVAILABLE_PAGES_ENUM.ADMINS_DETAIL },
      { path: `${p.admins.companyDetails}`, pageName: AVAILABLE_PAGES_ENUM.ADMINS_COMPANY_DETAILS },
      { path: `${p.admins.register}`, pageName: AVAILABLE_PAGES_ENUM.ADMINS_REGISTER, exact: true },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.AF_TOOL,
    [
      { path: p.afTool.index, pageName: AVAILABLE_PAGES_ENUM.AF_TOOL, exact: true },
      { path: `${p.afTool.index}/:calculation_id`, pageName: AVAILABLE_PAGES_ENUM.AF_TOOL_FORM_PAGE },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.CM,
    [
      { path: p.caseManagement.index, pageName: AVAILABLE_PAGES_ENUM.CM_INDEX, exact: true },
      { path: p.caseManagement.caseById, pageName: AVAILABLE_PAGES_ENUM.CM_CASE_BY_ID },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.COMMISSION_TERMS,
    [
      {
        path: p.commissionTerms.index,
        pageName: AVAILABLE_PAGES_ENUM.COMMISSION_TERMS_INDEX,
        exact: true,
      },
      { path: p.commissionTerms.create, pageName: AVAILABLE_PAGES_ENUM.COMMISSION_TERMS_CREATE },
      { path: `${p.commissionTerms.index}/:id`, pageName: AVAILABLE_PAGES_ENUM.COMMISSION_TERMS_EDIT },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.USER,
    [
      {
        path: p.user.edit,
        pageName: AVAILABLE_PAGES_ENUM.USER_EDIT,
      },
    ],
  ],
];

const publicGroupsMap: ProtectedGroupsMapType = [
  [
    ROUTE_GROUPS_ENUM.ADVISORS,
    [
      { path: `${p.advisers.add}`, pageName: AVAILABLE_PAGES_ENUM.ADVISOR_ADD, exact: true },
      { path: p.advisers.addSuccess, pageName: AVAILABLE_PAGES_ENUM.ADVISOR_ADD_SUCCESS },
      { path: p.advisers.search, pageName: AVAILABLE_PAGES_ENUM.ADVISOR_SEARCH },
      { path: `${p.advisers.list}/:id`, pageName: AVAILABLE_PAGES_ENUM.ADVISOR_LIST },
      { path: `${p.advisers.details}/:id`, pageName: AVAILABLE_PAGES_ENUM.ADVISOR_DETAIL },
    ],
  ],
  [
    ROUTE_GROUPS_ENUM.REGISTER,
    [
      { path: `${p.registerSetPassword}/:token`, pageName: AVAILABLE_PAGES_ENUM.REGISTER_SET_PASSWORD, exact: true },
      { path: `${p.registerVerification}/:token`, pageName: AVAILABLE_PAGES_ENUM.REGISTER_VERIFY, exact: true },
      { path: p.register, pageName: AVAILABLE_PAGES_ENUM.REGISTER },
    ],
  ],
];

const Routes: React.FC = () => {
  const bodyClasses: string[] = [];
  const user: User = useSelector((state: any) => state.auth.user);

  if (permissionService.hasLMCPermissions(user)) {
    bodyClasses.push('cm');
  }

  useBodyClass(...bodyClasses);

  return (
    <BrowserRouter>
      <LocationTracker />
      <ScrollToTop>
        <Switch>
          <Redirect exact from={p.main} to={p.login} />
          <Redirect from={p.registerFirmStep1} to={{ pathname: p.registerSearchFirms, state: { status: 301 } }} />;
          <Redirect from={p.registerFirmStep2} to={{ pathname: p.registerSearchFirms, state: { status: 301 } }} />;
          <Redirect from={p.registerFirmStep3} to={{ pathname: p.registerSearchFirms, state: { status: 301 } }} />;
          <Redirect from={p.registerFirmStep4} to={{ pathname: p.registerSearchFirms, state: { status: 301 } }} />;
          <Redirect from={p.registerSuccess} to={{ pathname: p.registerSearchFirms, state: { status: 301 } }} />;
          <Redirect from={p.advisers.add} to={{ pathname: p.registerSearchFirms, state: { status: 301 } }} />;
          {protectedGroupsMap.map(([routeGroup, protectedRoutesProps]) =>
            protectedRoutesProps.map((protectedRouteProps) => (
              <AuthenticatedProtectedRoute
                path={protectedRouteProps.path}
                routeGroup={routeGroup}
                pageName={protectedRouteProps.pageName}
                exact={protectedRouteProps.exact}
              />
            )),
          )}
          {publicGroupsMap.map(([routeGroup, publicRoutesProps]) =>
            publicRoutesProps.map((publicRouteProps) => (
              <AuthenticatedPublicRoute
                path={publicRouteProps.path}
                routeGroup={routeGroup}
                pageName={publicRouteProps.pageName}
                exact={publicRouteProps.exact}
              />
            )),
          )}
          <Route
            path={p.login}
            render={(routeProps: any) => (
              <LayoutProvider layout={SignIn.layout}>
                <SignIn {...routeProps} />
              </LayoutProvider>
            )}
          />
          <Route
            path={`${p.password.reset}/:id`}
            render={(routeProps: any) => (
              <LayoutProvider layout={ResetPassword.layout}>
                <ResetPassword {...routeProps} />
              </LayoutProvider>
            )}
          />
          <Route
            path={p.password.forgot}
            render={(routeProps: any) => (
              <LayoutProvider layout={ForgotPassword.layout}>
                <ForgotPassword {...routeProps} />
              </LayoutProvider>
            )}
          />
          <LayoutProvider>
            <Route path={p.logout} component={Logout} />
            <Route path={`${p.password.set}/:token`} component={SetPassword} />
            <Route path="*" component={PageNotFound} />
          </LayoutProvider>
        </Switch>
      </ScrollToTop>
    </BrowserRouter>
  );
};

export default Routes;
